import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionMeta,
  CreateFollowUpInput,
  FollowUp,
  FollowUpListQuery,
  UpdateFollowUpInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['follow-ups'] as const;

export interface FollowUpPage {
  items: FollowUp[];
  meta: CollectionMeta;
}

function toQuery(q: FollowUpListQuery): Record<string, string | number | boolean | undefined> {
  return {
    page: q.page,
    pageSize: q.pageSize,
    status: q.status,
    contactId: q.contactId,
    leadId: q.leadId,
    conversationId: q.conversationId,
    assignedUserId: q.assignedUserId,
    mine: q.mine ? 'true' : undefined,
    overdue: q.overdue ? 'true' : undefined,
    dueBefore: q.dueBefore,
    dueAfter: q.dueAfter,
    sortBy: q.sortBy,
    sortOrder: q.sortOrder,
  };
}

export async function listFollowUps(q: FollowUpListQuery): Promise<FollowUpPage> {
  const res = await apiClient.getCollection<FollowUp>('/follow-ups', { query: toQuery(q) });
  return { items: res.data, meta: res.meta };
}
export const createFollowUp = (input: CreateFollowUpInput) =>
  apiClient.post<FollowUp>('/follow-ups', input);
export const updateFollowUp = (id: string, patch: UpdateFollowUpInput) =>
  apiClient.patch<FollowUp>(`/follow-ups/${id}`, patch);
export const completeFollowUp = (id: string) =>
  apiClient.post<FollowUp>(`/follow-ups/${id}/complete`);
export const deleteFollowUp = (id: string) => apiClient.delete<void>(`/follow-ups/${id}`);

export function useFollowUps(query: FollowUpListQuery) {
  return useQuery<FollowUpPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listFollowUps(query),
  });
}

export function useCreateFollowUp() {
  const qc = useQueryClient();
  return useMutation<FollowUp, ApiClientError, CreateFollowUpInput>({
    mutationFn: createFollowUp,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateFollowUp() {
  const qc = useQueryClient();
  return useMutation<FollowUp, ApiClientError, { id: string; patch: UpdateFollowUpInput }>({
    mutationFn: ({ id, patch }) => updateFollowUp(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCompleteFollowUp() {
  const qc = useQueryClient();
  return useMutation<FollowUp, ApiClientError, string>({
    mutationFn: completeFollowUp,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteFollowUp() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteFollowUp,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
